export interface LatestBuild {
  id: number;
  fqdn: string | null;
  status: string;
  created_at: string | null;
  active: boolean;
  container_name: string | null;
  db_name: string | null;
  server_id: number | null;
  mailpit_fqdn: string | null;
  code_server_enabled: boolean;
}

export interface BranchWithBuild {
  id: number;
  name: string;
  branch_type: 'production' | 'staging' | 'development';
  latest_build: LatestBuild | null;
}

export interface AvailableBranch {
  id: number;
  name: string;
  branch_type: string;
}

export interface BranchesApiResponse {
  success: boolean;
  branches_with_builds: BranchWithBuild[];
  available_branches: AvailableBranch[];
  project: {
    id: number;
    name: string;
    github_repo: string | null;
    has_production_build: boolean;
  };
}

export type BranchType = 'production' | 'staging' | 'development';

// Status indicator helpers
export type StatusIndicator = 
  | 'solid-green'     // Success
  | 'solid-grey'      // Pending/Unknown
  | 'solid-red'       // Error
  | 'solid-yellow'    // Warning
  | 'blinking-green'  // Building
  | 'blinking-blue'   // Updating/Restoring
  | 'blinking-yellow' // Deleting
  | 'blinking-red';   // Active Test Failure

export function getStatusIndicator(status: string): StatusIndicator {
  const lowerStatus = status.toLowerCase();
  
  if (lowerStatus === 'success' || lowerStatus === 'healthy' || lowerStatus === 'running') {
    return 'solid-green';
  }
  if (lowerStatus === 'building' || lowerStatus === 'deploying' || lowerStatus === 'queued') {
    return 'blinking-green';
  }
  if (lowerStatus === 'updating' || lowerStatus === 'restoring') {
    return 'blinking-blue';
  }
  if (lowerStatus === 'deleting') {
    return 'blinking-yellow';
  }
  if (lowerStatus === 'error' || lowerStatus === 'failed' || lowerStatus === 'unhealthy' || lowerStatus === 'active error') {
    return 'solid-red';
  }
  if (lowerStatus === 'warning' || lowerStatus === 'degraded' || lowerStatus === 'stopped') {
    return 'solid-yellow';
  }
  if (lowerStatus === 'test_failed' || lowerStatus === 'test_failure') {
    return 'blinking-red';
  }
  
  return 'solid-grey';
}

// Branch History Types
export interface CommitInfo {
  id: number;
  commit_hash: string | null;
  message: string | null;
  author: string | null;
  timestamp: string | null;
}

export interface BuildInfo {
  id: number;
  fqdn: string | null;
  container_name: string | null;
  active: boolean;
  status: string | null;
  build_time: string | null;
  started_at: string | null;
  mailpit_fqdn: string | null;
  code_server_fqdn: string | null;
  code_server_enabled: boolean;
  has_test_results: boolean;
  odoo_version: string | null;
}

export interface CommitWithBuild {
  commit: CommitInfo;
  build: BuildInfo | null;
}

// Test Results
export interface TestResult {
  date: string;
  type: string;
  message: string;
}

export interface TestResultsResponse {
  success: boolean;
  is_building: boolean;
  test_results: TestResult[];
  build_status: string;
  total_count: number;
  error?: string;
}

export interface BranchHistoryResponse {
  success: boolean;
  branch: {
    id: number;
    name: string;
    branch_type: string;
    project: {
      id: number;
      code_server_enabled: boolean;
      github_repo: string | null;
    };
  };
  commits: CommitWithBuild[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_prev: boolean;
    has_next: boolean;
    prev_num: number | null;
    next_num: number | null;
  };
}

// Remote IDE / code-server
export interface CodeServerTokenResponse {
  login_url: string;
  token: string;
  code_server_url?: string;
}

export interface SshTunnelInfo {
  tunnel_id?: string;
  host: string;
  port: number | string;
  username: string;
  expires_at?: string;
  connection_string?: string;
  vs_code_command?: string;
  vs_code_command_simple?: string;
  private_key?: string;
  use_personal_key?: boolean;
}

export interface SshTunnelResponse {
  success: boolean;
  tunnel?: SshTunnelInfo;
  error?: string;
  // Optional debug information for internal troubleshooting; not shown to users.
  debug_error?: string;
}

export interface SshKeysCheckResponse {
  success: boolean;
  has_keys: boolean;
  key_count?: number;
  error?: string;
}
